import isEmpty from 'lodash/isEmpty';

declare global {
  interface Window {
    desktop?: {
      mode: string;
      /**
       * Electron `process.platform` when the shell exposes it
       * (`darwin` | `win32` | …). Older desktop builds may omit this.
       */
      platform?: string;
      openLinkInBrowser: (url: string) => void;
      openSamlLogin?: (
        url: string
      ) => Promise<{ ok: boolean; errorCode?: string }>;
      triggerSystemNotification?: (options: Notification) => void;
      handleSystemNotificationAction?: (
        callback: (notification: Notification, action: number) => void
      ) => void;
      handleSystemNotificationClick?: (
        callback: (notification: Notification) => void
      ) => void;
      removeSystemNotificationListeners?: () => void;
      changeMode?: (mode: string, initialPath?: string) => void;
      recording?: {
        start: (meetingUuid?: string) => Promise<DesktopRecordingResult>;
        stop: (
          options?: DesktopRecordingStopOptions
        ) => Promise<DesktopRecordingResult>;
        pause?: () => Promise<DesktopRecordingResult>;
        /** Alias for pause — some Electron bridges drop nested `pause`. */
        pauseRecording?: () => Promise<DesktopRecordingResult>;
        resume?: () => Promise<DesktopRecordingResult>;
        resumeRecording?: () => Promise<DesktopRecordingResult>;
        status: () => Promise<DesktopRecordingStatus>;
      };
      pauseRecording?: () => Promise<DesktopRecordingResult>;
      resumeRecording?: () => Promise<DesktopRecordingResult>;
    };
    avomaHistory?: {
      push: (path: string) => void;
      replace: (path: string) => void;
      goBack: () => void;
    };
  }
}

export interface DesktopRecordingResult {
  ok: boolean;
  sessionId?: string;
  meetingUuid?: string;
  localAudioPath?: string;
  durationMs?: number;
  bytes?: number;
  errorCode?: string;
  errorMessage?: string;
}

export interface DesktopRecordingStatus {
  state: string;
  sessionId?: string;
  localAudioPath?: string;
  meetingUuid?: string | null;
  /** Elapsed capture time when the desktop daemon reports an active session. */
  durationMs?: number;
  /** Epoch ms or ISO start time for the active session, when known. */
  startedAt?: number | string;
}

/** Distinct from idle: status IPC failed; UI should keep its local session. */
export const DESKTOP_RECORDING_STATUS_UNAVAILABLE = 'unavailable';

/** Mirrors Electron `AVOMA_DESKTOP_RECORDING_CHANGED_EVENT`. */
export const AVOMA_DESKTOP_RECORDING_CHANGED_EVENT =
  'avoma-desktop-recording-changed';

/**
 * Mirrors Electron `DESKTOP_REQUEST_LICENSE_QUERY_PARAM`. Desktop's native
 * upgrade / limit-reached overlays send members here (value = feature key) to
 * open the request-license dialog — DesktopRequestLicenseDialog strips it.
 */
export const DESKTOP_REQUEST_LICENSE_QUERY_PARAM = 'desktop_request_license';

export interface DesktopRecordingStopOptions {
  /**
   * When true, Electron skips the "Stop recording?" guardrail.
   * Leave false/omitted for user-initiated Zen / FE stop so the desktop modal shows.
   */
  skipConfirm?: boolean;
  navigateToMeeting?: boolean;
}

const notDesktopRecordingResult = (): DesktopRecordingResult => ({
  ok: false,
  errorCode: 'NOT_DESKTOP',
  errorMessage:
    'Desktop recording is only available in the Avoma desktop app.'
});

const toRecordingFailure = (
  error: unknown,
  errorCode: string,
  fallbackMessage: string
): DesktopRecordingResult => ({
  ok: false,
  errorCode,
  errorMessage: error instanceof Error ? error.message : fallbackMessage
});

export interface Notification {
  id: string;
  title: string;
  subtitle?: string;
  body?: string;
  actions: Array<{ type: string; text: string }>;
}

export const desktopAppModes = {
  ZEN_MODE: 'zen',
  FULL_MODE: 'full'
};

/**
 * Path Electron Zen loads for the latest Minimal UI (Ask Avoma card, row
 * recording actions, etc.). Old Electron builds still load
 * {@link DESKTOP_ZEN_V1_PATH} until users update the desktop app.
 */
export const DESKTOP_ZEN_V2_PATH = 'meetings/views/zen';

/** Legacy Zen list path — old Electron default; serves Minimal V1. */
export const DESKTOP_ZEN_V1_PATH = 'meetings/views/my';

/** Reserved slug under `/meetings/views/:pinnedviewUuid` for Zen V2. */
export const DESKTOP_ZEN_V2_VIEW_SLUG = 'zen';

export const isDesktopContext = () => !isEmpty(window.desktop);

export const isFullMode = (): boolean =>
  window.desktop?.mode === desktopAppModes.FULL_MODE;

export const isZenMode = (): boolean =>
  window.desktop?.mode === desktopAppModes.ZEN_MODE;

/**
 * True only inside the desktop shell on Windows. Never true for the web app.
 * Prefers `window.desktop.platform`; falls back to userAgent when an older
 * Electron build omits `platform`.
 */
export const isDesktopWindows = (): boolean => {
  if (!isDesktopContext()) {
    return false;
  }
  const platform = window.desktop?.platform;
  if (typeof platform === 'string' && platform.length > 0) {
    return platform === 'win32';
  }
  return /Windows/i.test(navigator.userAgent);
};

/**
 * True when the SPA location is the Zen V2 list route
 * (`/meetings/views/zen` or nested under that slug).
 */
export const isDesktopZenV2Path = (
  pathname: string | undefined | null
): boolean => {
  if (!pathname) return false;
  return (
    pathname === `/${DESKTOP_ZEN_V2_PATH}` ||
    pathname.startsWith(`/${DESKTOP_ZEN_V2_PATH}/`) ||
    pathname === `/meetings/views/${DESKTOP_ZEN_V2_VIEW_SLUG}` ||
    pathname.startsWith(`/meetings/views/${DESKTOP_ZEN_V2_VIEW_SLUG}/`)
  );
};

export const isDesktopZenV1Path = (
  pathname: string | undefined | null
): boolean => {
  if (!pathname) return false;
  return (
    pathname === `/${DESKTOP_ZEN_V1_PATH}` ||
    pathname.startsWith(`/${DESKTOP_ZEN_V1_PATH}/`)
  );
};

/** sessionStorage key for the Zen list the user last opened (V1 vs V2). */
export const DESKTOP_ZEN_LIST_PATH_STORAGE_KEY = 'avomaDesktopZenListPath';

const stripTrailingSlash = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/')
    ? pathname.replace(/\/+$/, '')
    : pathname;

/**
 * Canonical Zen list path for a location (`/meetings/views/zen` or
 * `/meetings/views/my`). Null when the path is not a Zen list.
 */
export const zenListPathFromLocation = (
  pathname: string | undefined | null
): string | null => {
  if (!pathname) return null;
  const path = stripTrailingSlash(pathname.split('?')[0] || pathname);
  if (isDesktopZenV2Path(path)) return `/${DESKTOP_ZEN_V2_PATH}`;
  if (isDesktopZenV1Path(path)) return `/${DESKTOP_ZEN_V1_PATH}`;
  return null;
};

/** Remember which Zen list was open so meeting-details Back can return there. */
export const rememberDesktopZenListPath = (
  pathname: string | undefined | null
): void => {
  const listPath = zenListPathFromLocation(pathname);
  if (!listPath) return;
  try {
    sessionStorage.setItem(DESKTOP_ZEN_LIST_PATH_STORAGE_KEY, listPath);
  } catch {
    // Ignore quota / private-mode failures; fallback path still works.
  }
};

/**
 * Zen list to return to from meeting details after Stop. Prefers the list
 * this session opened; otherwise the current Electron default (V2).
 */
export const getDesktopZenListPath = (): string => {
  try {
    const stored = sessionStorage.getItem(DESKTOP_ZEN_LIST_PATH_STORAGE_KEY);
    const listPath = zenListPathFromLocation(stored);
    if (listPath) return listPath;
  } catch {
    // sessionStorage unavailable
  }
  return `/${DESKTOP_ZEN_V2_PATH}`;
};

export const canRecordFromDesktop = (): boolean =>
  typeof window.desktop?.recording?.start === 'function';

/**
 * Invokes the desktop recorder over IPC. Passing `meetingUuid` attaches the
 * capture to an existing meeting (the row case); omitting it lets the desktop
 * app create a fresh meeting shell (the impromptu / top-record case).
 * Returns a structured result even when not running inside the desktop app, so
 * callers can surface a friendly message instead of throwing.
 */
export const startDesktopRecording = async (
  meetingUuid?: string
): Promise<DesktopRecordingResult> => {
  if (!canRecordFromDesktop()) {
    return notDesktopRecordingResult();
  }
  try {
    return await window.desktop!.recording!.start(meetingUuid);
  } catch (error) {
    return toRecordingFailure(
      error,
      'RECORDING_START_FAILED',
      'Failed to start recording.'
    );
  }
};

const hasDesktopRecordingBridge = (): boolean =>
  Boolean(window.desktop?.recording);

const hasDesktopPauseOperation = (): boolean => {
  const desktop = window.desktop;
  const recording = desktop?.recording;
  return Boolean(
    recording &&
      (recording.pause || recording.pauseRecording || desktop?.pauseRecording)
  );
};

const hasDesktopResumeOperation = (): boolean => {
  const desktop = window.desktop;
  const recording = desktop?.recording;
  return Boolean(
    recording &&
      (recording.resume ||
        recording.resumeRecording ||
        desktop?.resumeRecording)
  );
};

/**
 * True when Zen/Full can send pause over the desktop recording bridge.
 * Require a pause operation to exist so controls stay hidden when pause is
 * unavailable. Do not require `typeof pause === 'function'` — Electron
 * contextBridge can expose nested methods that fail that check while
 * `recording.pause()` works.
 */
export const canPauseDesktopRecording = (): boolean =>
  hasDesktopRecordingBridge() && hasDesktopPauseOperation();

export const canResumeDesktopRecording = (): boolean =>
  hasDesktopRecordingBridge() && hasDesktopResumeOperation();

export const canStopDesktopRecording = (): boolean =>
  hasDesktopRecordingBridge();

export const canGetDesktopRecordingStatus = (): boolean =>
  hasDesktopRecordingBridge();

export const getDesktopRecordingStatus =
  async (): Promise<DesktopRecordingStatus> => {
    if (!canGetDesktopRecordingStatus()) {
      return { state: 'idle' };
    }
    try {
      // Call on the bridge object (do not extract). Electron proxies often
      // fail `typeof status === 'function'` while `recording.status()` works.
      return await window.desktop!.recording!.status();
    } catch {
      return { state: DESKTOP_RECORDING_STATUS_UNAVAILABLE };
    }
  };

/**
 * Call a contextBridge method on the object (do not extract it first).
 * Electron proxies often only work as `obj.method()`, not `const fn = obj.method; fn()`.
 */
const invokeDesktopRecordingPhaseChange = async ({
  nestedPrimary,
  nestedFallback,
  topLevelFallback,
  errorCode,
  fallbackMessage
}: {
  nestedPrimary: 'pause' | 'resume';
  nestedFallback: 'pauseRecording' | 'resumeRecording';
  topLevelFallback: 'pauseRecording' | 'resumeRecording';
  errorCode: string;
  fallbackMessage: string;
}): Promise<DesktopRecordingResult> => {
  const desktop = window.desktop;
  const recording = desktop?.recording;
  if (!desktop) {
    return notDesktopRecordingResult();
  }

  try {
    if (recording) {
      try {
        const result = await recording[nestedPrimary]?.();
        if (result != null) {
          return result;
        }
      } catch (primaryError) {
        if (!recording[nestedFallback] && !desktop[topLevelFallback]) {
          throw primaryError;
        }
      }
      if (recording[nestedFallback]) {
        return await recording[nestedFallback]();
      }
    }
    if (desktop[topLevelFallback]) {
      return await desktop[topLevelFallback]();
    }
    return {
      ok: false,
      errorCode,
      errorMessage: fallbackMessage
    };
  } catch (error) {
    return toRecordingFailure(error, errorCode, fallbackMessage);
  }
};

const invokePauseOnDesktop = async (): Promise<DesktopRecordingResult> =>
  invokeDesktopRecordingPhaseChange({
    nestedPrimary: 'pause',
    nestedFallback: 'pauseRecording',
    topLevelFallback: 'pauseRecording',
    errorCode: 'RECORDING_PAUSE_FAILED',
    fallbackMessage: 'Failed to pause recording.'
  });

const invokeResumeOnDesktop = async (): Promise<DesktopRecordingResult> =>
  invokeDesktopRecordingPhaseChange({
    nestedPrimary: 'resume',
    nestedFallback: 'resumeRecording',
    topLevelFallback: 'resumeRecording',
    errorCode: 'RECORDING_RESUME_FAILED',
    fallbackMessage: 'Failed to resume recording.'
  });

export const pauseDesktopRecording =
  async (): Promise<DesktopRecordingResult> => invokePauseOnDesktop();

export const resumeDesktopRecording =
  async (): Promise<DesktopRecordingResult> => invokeResumeOnDesktop();

/**
 * Stops desktop capture. By default Electron shows the same "Stop recording?"
 * guardrail used by the recording widget. Pass `{ skipConfirm: true }` only
 * for non-interactive / crash paths that must not prompt.
 */
export const stopDesktopRecording = async (
  options: DesktopRecordingStopOptions = {}
): Promise<DesktopRecordingResult> => {
  if (!canStopDesktopRecording()) {
    return notDesktopRecordingResult();
  }
  try {
    return await window.desktop!.recording!.stop(options);
  } catch (error) {
    return toRecordingFailure(
      error,
      'RECORDING_STOP_FAILED',
      'Failed to stop recording.'
    );
  }
};
